import { TRPCClientError } from '@trpc/client';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc.js';
import {
  buildSurchargeRuleSetInput,
  emptySurchargeRuleDraft,
  type SurchargeConditionKind,
  type SurchargeRuleDraft,
  type SurchargeRuleSetDraft,
} from '../../lib/surcharge-rule-set-form.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.js';
import { Field } from '../ui/field.js';
import { Input } from '../ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select.js';
import { Textarea } from '../ui/textarea.js';

const GLOBAL_SCOPE = '__global__';
const WEEKDAYS = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
  { value: 0, label: 'So' },
] as const;

function localToday(): string {
  return new Intl.DateTimeFormat('sv-SE').format(new Date());
}

function initialDraft(): SurchargeRuleSetDraft {
  return {
    name: '',
    base: 'wage',
    stackingPolicy: 'highest_only',
    validFrom: localToday(),
    notes: '',
    rules: [emptySurchargeRuleDraft()],
  };
}

function validationMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'issues' in error) {
    const issues = (error as { issues?: Array<{ message?: string }> }).issues;
    if (issues?.[0]?.message) return issues[0].message;
  }
  return error instanceof Error ? error.message : 'Eingabe konnte nicht validiert werden.';
}

export function SurchargeRuleSetForm({
  clients,
  onCreated,
}: {
  clients: readonly { id: string; companyName: string | null }[];
  onCreated?: (ruleSetId: string) => void;
}) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState(initialDraft);
  const [formError, setFormError] = useState<string | null>(null);
  const create = trpc.surchargeRuleSets.create.useMutation({
    onSuccess: async (created) => {
      await utils.surchargeRuleSets.list.invalidate();
      setDraft(initialDraft());
      setFormError(null);
      toast.success('Neue Regelwerk-Version angelegt.');
      onCreated?.(created.id);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateDraft = (patch: Partial<SurchargeRuleSetDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };
  const updateRule = (index: number, patch: Partial<SurchargeRuleDraft>) => {
    setDraft((current) => ({
      ...current,
      rules: current.rules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule,
      ),
    }));
  };
  const toggleWeekday = (index: number, day: number) => {
    const current = draft.rules[index];
    if (!current) return;
    updateRule(index, {
      weekdays: current.weekdays.includes(day)
        ? current.weekdays.filter((value) => value !== day)
        : [...current.weekdays, day],
    });
  };

  const submit = async () => {
    setFormError(null);
    try {
      await create.mutateAsync(buildSurchargeRuleSetInput(draft));
    } catch (error) {
      // TRPC-Fehler meldet bereits der onError-Toast; lokale Validierungs-
      // fehler (Zod/Sondertag-Parser) müssen IMMER inline erscheinen — ein
      // Guard über `create.error` wäre ein stale Closure-Wert des letzten
      // Renders und würde sie nach einem früheren Server-Fehler verschlucken.
      if (!(error instanceof TRPCClientError)) setFormError(validationMessage(error));
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Field label="Regelwerk-Name" htmlFor="rule-set-name">
          <Input
            id="rule-set-name"
            value={draft.name}
            onChange={(event) => updateDraft({ name: event.target.value })}
            placeholder="z. B. BAP Lohnzuschläge"
            required
          />
        </Field>
        <Field label="Berechnungsbasis" htmlFor="rule-set-base">
          <Select
            value={draft.base}
            onValueChange={(value) => updateDraft({ base: value as SurchargeRuleSetDraft['base'] })}
          >
            <SelectTrigger id="rule-set-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="wage">Worker-Lohn</SelectItem>
              <SelectItem value="bill_rate">Kunden-Verrechnungssatz</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field
          label="Scope"
          htmlFor="rule-set-client"
          hint="Global gilt für alle; eine Firma grenzt das Regelwerk auf diesen Kunden ein."
        >
          <Select
            value={draft.clientId ?? GLOBAL_SCOPE}
            onValueChange={(value) =>
              updateDraft({ clientId: value === GLOBAL_SCOPE ? undefined : value })
            }
          >
            <SelectTrigger id="rule-set-client">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL_SCOPE}>Global</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.companyName ?? '(unbenannter Kunde)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Gültig ab" htmlFor="rule-set-valid-from">
          <Input
            id="rule-set-valid-from"
            type="date"
            value={draft.validFrom}
            onChange={(event) => updateDraft({ validFrom: event.target.value })}
            required
          />
        </Field>
        <Field label="Stacking" htmlFor="rule-set-stacking">
          <Select
            value={draft.stackingPolicy}
            onValueChange={(value) =>
              updateDraft({ stackingPolicy: value as SurchargeRuleSetDraft['stackingPolicy'] })
            }
          >
            <SelectTrigger id="rule-set-stacking">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="highest_only">Nur höchster Zuschlag</SelectItem>
              <SelectItem value="cumulative">Zuschläge addieren</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Notiz" htmlFor="rule-set-notes">
          <Textarea
            id="rule-set-notes"
            value={draft.notes}
            onChange={(event) => updateDraft({ notes: event.target.value })}
            placeholder="Quelle, Tarifstand oder Änderungsgrund"
            className="min-h-9"
          />
        </Field>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold">Regeln</h3>
            <p className="text-sm text-[var(--muted-foreground)]">
              Reihenfolge ist relevant, wenn bei gleichem Prozentsatz nur die höchste Regel greift.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => updateDraft({ rules: [...draft.rules, emptySurchargeRuleDraft()] })}
          >
            + Regel
          </Button>
        </div>

        {draft.rules.map((rule, index) => (
          <Card key={index}>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle>Regel {index + 1}</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={draft.rules.length === 1}
                onClick={() =>
                  updateDraft({ rules: draft.rules.filter((_, ruleIndex) => ruleIndex !== index) })
                }
              >
                Entfernen
              </Button>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Regel-Key" htmlFor={`rule-key-${index}`}>
                <Input
                  id={`rule-key-${index}`}
                  value={rule.key}
                  onChange={(event) => updateRule(index, { key: event.target.value })}
                  placeholder="night-25"
                  required
                />
              </Field>
              <Field label="Prozent" htmlFor={`rule-percent-${index}`}>
                <Input
                  id={`rule-percent-${index}`}
                  type="number"
                  min="0"
                  max="1000"
                  step="0.01"
                  value={rule.percent}
                  onChange={(event) => updateRule(index, { percent: event.target.value })}
                  required
                />
              </Field>
              <Field label="Bedingung" htmlFor={`rule-condition-${index}`}>
                <Select
                  value={rule.conditionKind}
                  onValueChange={(value) =>
                    updateRule(index, { conditionKind: value as SurchargeConditionKind })
                  }
                >
                  <SelectTrigger id={`rule-condition-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">Immer</SelectItem>
                    <SelectItem value="weekdays">Wochentage</SelectItem>
                    <SelectItem value="holiday">Gesetzlicher Feiertag</SelectItem>
                    <SelectItem value="special_days">Sondertage</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="space-y-2">
                <span className="text-sm font-medium">Optionen</span>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rule.useWindow}
                    onChange={(event) => updateRule(index, { useWindow: event.target.checked })}
                  />
                  Zeitfenster verwenden
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rule.onlyIfShiftStartedBeforeMidnight}
                    onChange={(event) =>
                      updateRule(index, { onlyIfShiftStartedBeforeMidnight: event.target.checked })
                    }
                  />
                  Schichtstart vor 00:00 nötig
                </label>
              </div>

              {rule.useWindow && (
                <>
                  <Field label="Fenster von" htmlFor={`rule-window-from-${index}`}>
                    <Input
                      id={`rule-window-from-${index}`}
                      type="time"
                      value={rule.windowFrom}
                      onChange={(event) => updateRule(index, { windowFrom: event.target.value })}
                      required
                    />
                  </Field>
                  <Field label="Fenster bis" htmlFor={`rule-window-to-${index}`}>
                    <Input
                      id={`rule-window-to-${index}`}
                      type="time"
                      value={rule.windowTo}
                      onChange={(event) => updateRule(index, { windowTo: event.target.value })}
                      required
                    />
                  </Field>
                </>
              )}

              {rule.conditionKind === 'weekdays' && (
                <div className="space-y-2 md:col-span-2">
                  <span className="text-sm font-medium">Wochentage</span>
                  <div className="flex flex-wrap gap-3">
                    {WEEKDAYS.map((day) => (
                      <label key={day.value} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={rule.weekdays.includes(day.value)}
                          onChange={() => toggleWeekday(index, day.value)}
                        />
                        {day.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {rule.conditionKind === 'special_days' && (
                <div className="md:col-span-2 xl:col-span-4">
                  <Field
                    label="Sondertage"
                    htmlFor={`rule-special-days-${index}`}
                    hint="Kommagetrennt: MM-TT oder MM-TT@HH:MM, z. B. 12-24@14:00, 12-31@14:00."
                  >
                    <Input
                      id={`rule-special-days-${index}`}
                      value={rule.specialDays}
                      onChange={(event) => updateRule(index, { specialDays: event.target.value })}
                      placeholder="12-24@14:00, 12-31@14:00"
                      required
                    />
                  </Field>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
        <p className="text-sm text-[var(--muted-foreground)]">
          Korrekturen erzeugen eine neue Version; bestehende Finanzhistorie wird nicht überschrieben.
        </p>
        <div className="flex items-center gap-3">
          {formError && <p className="max-w-lg text-sm text-[oklch(0.55_0.2_25)]">{formError}</p>}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Speichere …' : 'Version anlegen'}
          </Button>
        </div>
      </div>
    </form>
  );
}
